import React, { useEffect, useRef, useState } from 'react';
import { Sheet } from '@mui/joy';
import { Package as PackageIcon } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { catalogApi } from '../../lib/distributorsApi';

interface Props {
  distributorId: number;
  assetId?: string;
  alt?: string;
  size?: number;
}

/**
 * Product thumbnail for API catalog items.
 *
 * ABC image assets require a Bearer token, and access tokens live in memory —
 * a plain <img src> can never authenticate. So the bytes are blob-fetched
 * through the backend proxy and rendered via an object URL. Items without an
 * asset id render a placeholder with zero network.
 */
export default function CatalogItemImage({ distributorId, assetId, alt, size = 48 }: Props) {
  const container = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!container.current) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true);
        observer.disconnect();
      }
    }, { rootMargin: '100px' });
    observer.observe(container.current);
    return () => observer.disconnect();
  }, []);

  const { data: blob } = useQuery({
    queryKey: ['catalog-item-image', distributorId, assetId],
    queryFn: ({ signal }) => catalogApi.getItemImage(distributorId, assetId!, signal),
    enabled: !!assetId && visible,
    staleTime: 0,
    gcTime: 0,
    // The loader handles bounded 429 retries and a shared pause. Refocusing
    // the tab must not restart every image request on the current page.
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    const url = blob ? URL.createObjectURL(blob) : null;
    setObjectUrl(url);
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [blob]);

  return (
    <Sheet
      ref={container}
      variant="soft"
      sx={{ width: size, height: size, borderRadius: 'sm', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
    >
      {assetId && objectUrl ? (
        <img
          src={objectUrl}
          alt={alt || 'Product'}
          width={size}
          height={size}
          onError={() => setObjectUrl(null)}
          style={{ borderRadius: 6, objectFit: 'contain' }}
        />
      ) : <PackageIcon size={size * 0.5} color="var(--joy-palette-neutral-400)" />}
    </Sheet>
  );
}
